/**
 * 开奖台：把开出的号和我的号逐位比出来。
 *
 * <p>开奖期间选号器反正不能用（闸门挡着），于是它整块变成开奖台 —— 而这不只是找块
 * 空地：选号器每一格下面本来就写着这一档值多少钱（`Jackpot / R$ 600 / R$ 40 / R$ 4`），
 * 开奖又正好从右往左落。数字落进某一格的瞬间，它值多少钱就写在正下方。
 * 规则不用讲，它被看见。
 */

import { roundSeqFromRound } from '../game/roundLabel.ts';

/**
 * 开奖台上那一期的日内期号（`#194`）—— 正在开的那一期，或结果定格着的那一期，
 * **不是**正在接单的那一期。
 *
 * <p><b>钉的是这个投诉：「194 期还没开出来，上面就显示 195 期了」。</b>后端在上一期
 * 封盘的那一刻就把 `current` 轮到了下一期（下一期立刻开始接单，规则见后端
 * `PREV_ROUND_DRAWING`）。于是 194 还在逐位落号时，`status.round.label` 已经是 195。
 * 页头那条期号必须跟着开奖台走 —— 用户此刻关心的是 194 的结果，等 194 落定、闸门
 * 放开，才轮到 195 的投注。这条和下面的开奖台、倒计时条本来就已经定在被开的那一期上，
 * 只有页头的期号条漏了。
 *
 * <p>优先用后端在 `recentResults` 里给的 `label`（权威，与档案/注单历史同一条规则）；
 * 结果还没落进 `recentResults`（Computing 阶段）时，就从被开那一期的 `ends_at` 按
 * 同一套开奖时区 + cadence 规则（{@link roundSeqFromRound}）算日内序号 —— 于是它和
 * 开着那一期的 `label` 是同一种编号（5 分钟档 #001-288，整点档 #01-24），不会各说各话。
 *
 * @param stageRound 开奖台上那一期的全局期号（`held.round` 或 `drawing.round`）
 * @param recentResults 最近结果（含 `label`）；落定后这里就有被开那一期
 * @param drawingEndsAt `drawing.endsAt` —— 被开那一期的收盘时刻（右开边界）
 * @param slotSec 每期秒数（5 分钟档 300，整点档 3600）
 * @param timeZone 开奖时区；不传则用全局锚点（与后端算 label 同一个时区）
 */
export function stageRoundIdLabel(
  stageRound: number | null | undefined,
  recentResults: readonly { round: number; label: string }[] | undefined,
  drawingEndsAt: number | null | undefined,
  slotSec: number,
  timeZone?: string,
): string {
  if (!stageRound) return '';
  const known = recentResults?.find((row) => row.round === stageRound)?.label;
  if (known) return known;
  if (drawingEndsAt) {
    const seq = roundSeqFromRound({ ends_at: drawingEndsAt }, slotSec, timeZone);
    return seq ? `#${seq}` : '';
  }
  return '';
}

/**
 * 从最后一位起的**连续**命中数，只统计已经落定的那几位。
 *
 * <p><b>这是整个功能唯一可能造成真实伤害的地方。</b>奖级要的是末尾连续匹配：
 * 开出 `4738`、我的号 `4999`，第一位的 `4` 确实一样，但这注**一分钱不值**。
 * 若按「有几位相同」来放特效，就会在一注全输的号上放庆祝动画。
 *
 * <p>所以只认从右往左的连续段，<b>断了就停</b>，后面再对上也不算。
 *
 * @param landed 已落定的位数（从右数）。落定过程中据此逐步升级，不提前剧透。
 */
export function suffixHits(
  pick: string | null | undefined,
  result: string | null | undefined,
  landed: number,
): number {
  const p = String(pick ?? '');
  const r = String(result ?? '');
  if (!p || !r || p.length !== r.length) return 0;
  const window = Math.max(0, Math.min(Math.floor(landed), r.length));
  let hits = 0;
  for (let i = 0; i < window; i += 1) {
    const at = r.length - 1 - i;
    if (p[at] !== r[at]) break; // 断了就是断了
    hits += 1;
  }
  return hits;
}

export type StageTicket = { id: number; pick: string };

/**
 * 此刻表现最好的那一张票。
 *
 * <p>一期最多 20 张，全列出来会把「升温」这件事拆碎成几十行。只顶一张上去，
 * 每落定一位重算一次 —— 屏幕上永远只有一行在跟着开奖走。
 *
 * <p>并列时取**先买的那张**（id 小）：同样成绩下，先下注的人排在前面是唯一
 * 不需要解释的规则。
 */
export function bestTicket(
  tickets: readonly StageTicket[] | undefined,
  result: string | null | undefined,
  landed: number,
): { ticket: StageTicket; hits: number } | null {
  if (!tickets || !tickets.length) return null;
  let best: { ticket: StageTicket; hits: number } | null = null;
  for (const ticket of tickets) {
    const hits = suffixHits(ticket.pick, result, landed);
    if (!best || hits > best.hits || (hits === best.hits && ticket.id < best.ticket.id)) {
      best = { ticket, hits };
    }
  }
  return best;
}

export type HitTier = 'none' | 'low' | 'mid' | 'high' | 'jackpot';

/**
 * 特效强度。**必须贴着真实赔付走**。
 *
 * <p>中末 1 位只值 R$ 4 —— 给它放烟花是在教用户「中一位很了不起」。强度按档位递增，
 * 全中才给到顶：头奖是这个玩法存在的理由，它得跟前三档拉开数量级，
 * 否则中一位和中头奖在屏幕上长得差不多，头奖那一刻就不值钱了。
 */
export function hitTier(hits: number, digits: number): HitTier {
  const n = Math.max(0, Math.floor(hits));
  if (n <= 0) return 'none';
  if (n >= digits) return 'jackpot';
  if (n === 1) return 'low';
  return n === digits - 1 ? 'high' : 'mid';
}

/**
 * 对照行列几张 —— <b>全部</b>。
 *
 * <p>原来封顶 5 行，但玩家买了十几注只看到 5 行（2026-08-20 真机反馈）：他要的是
 * 核对自己买了什么，少一行就核对不完。而且封顶还有个更糟的后果 —— 落定之前排序
 * 是按买入顺序的，中的那张很可能排在第 6 位，于是**恰恰是最该看到的那张被折叠掉**，
 * 等它靠命中浮上来时玩家已经错过了前半段。
 *
 * <p>这一排本来就在页面中部、上下都能滚，长一点不致命；看不全才致命。
 */
export const STAGE_ROWS = Number.POSITIVE_INFINITY;

export type RankedTicket = { ticket: StageTicket; hits: number; sameCount: number };

/**
 * 我在这一期的票，按此刻表现排好序。
 *
 * <p><b>为什么不是只显示一张</b>（2026-08-19 真机反馈）：落定之前所有票都是 0 命中，
 * 「最好的那张」退化成「买得最早的那张」——屏幕上就是一张随机的票。玩家买了 9 个
 * 不同的号，看到的却只有一个，而且不是后来中的那个。他要的是**比较**，一张比不了。
 *
 * <p>排序：命中多的在前；并列取先买的（id 小）。于是随着数字逐位落定，中的那张
 * 会自己浮上来 —— 排序本身就是一次演出。
 *
 * <p>同号多张合成一行并带上张数：quantity=10 时十行一模一样的号毫无信息。
 */
export function rankedTickets(
  tickets: readonly StageTicket[] | undefined,
  result: string | null | undefined,
  landed: number,
  limit = STAGE_ROWS,
): { rows: RankedTicket[]; hidden: number } {
  if (!tickets || !tickets.length) return { rows: [], hidden: 0 };
  const byPick = new Map<string, RankedTicket>();
  for (const ticket of tickets) {
    const seen = byPick.get(ticket.pick);
    if (seen) {
      seen.sameCount += 1;
      if (ticket.id < seen.ticket.id) seen.ticket = ticket;
      continue;
    }
    byPick.set(ticket.pick, {
      ticket, hits: suffixHits(ticket.pick, result, landed), sameCount: 1,
    });
  }
  const all = [...byPick.values()]
    .sort((a, b) => b.hits - a.hits || a.ticket.id - b.ticket.id);
  return { rows: all.slice(0, limit), hidden: Math.max(0, all.length - limit) };
}

/**
 * 烟花的规模，按奖级给。
 *
 * <p>「小奖小烟花，大奖大烟花」（2026-08-20 评审）。规模必须跟着**赔付**走而不是
 * 跟着「中了」这个事实走：中末 1 位只值 R$ 4，给它放和头奖一样的烟花，头奖那一刻
 * 就不值钱了 —— 这和 hitTier 是同一条原则的延伸。
 *
 * <p>粒子数刻意拉开数量级（12 → 20 → 32 → 56），因为人眼对「多了一点」不敏感，
 * 对「明显更多」才有反应。
 */
export function fireworkCount(tier: HitTier): number {
  switch (tier) {
    case 'low': return 12;
    case 'mid': return 20;
    case 'high': return 32;
    case 'jackpot': return 56;
    default: return 0;
  }
}

/** 一颗粒子的落点，极坐标转直角。用下标派生，不用随机数——同样的档位每次长得一样。 */
export function fireworkParticles(count: number, spreadPx: number): {
  dx: number; dy: number; delayMs: number;
}[] {
  const n = Math.max(0, Math.floor(count));
  // 黄金角散布，比等分角好看：等分角在粒子少时会排成一个明显的多边形
  const GOLDEN = 2.399963;
  return Array.from({ length: n }, (_, i) => {
    const angle = i * GOLDEN;
    // sqrt 让粒子在圆面上均匀，而不是全挤在中心
    const radius = spreadPx * Math.sqrt((i + 1) / n);
    return {
      dx: Math.round(Math.cos(angle) * radius),
      dy: Math.round(Math.sin(angle) * radius),
      delayMs: (i % 6) * 40,
    };
  });
}
